import React, { useEffect } from "react";
import { X, ArrowRight, Award, Users, Globe, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import InstagramFeed from "./InstagramFeed";

const ArtistModal = ({ artist, onClose }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!artist) return null;

  const statIcons = [<Calendar size={14} key="c" />, <Users size={14} key="u" />, <Globe size={14} key="g" />, <Award size={14} key="a" />];

  const handleBook = () => {
    onClose();
    navigate(`/contact?artistId=${artist.id}`);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 animate-fade-in-up"
      style={{ animationDuration: "0.3s" }}
      data-testid="artist-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative glass-dark rounded-sm w-full max-w-5xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-sm border border-gold/30 bg-black/50 hover:bg-gold/20 hover:border-gold text-white/70 hover:text-gold transition-all flex items-center justify-center backdrop-blur-sm"
          aria-label="Close"
          data-testid="artist-modal-close"
        >
          <X size={18} />
        </button>

        {/* Hero image */}
        <div className="relative h-[40vh] min-h-[280px] overflow-hidden">
          <img
            src={artist.image || "/images/artist-1.jpg"}
            alt={artist.name}
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 22%" }}
            onError={(e) => { e.currentTarget.src = "/images/artist-1.jpg"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/95" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="flex flex-wrap gap-2 mb-3">
              {artist.specialties.map((s) => (
                <span key={s} className="specialty-tag">{s}</span>
              ))}
            </div>
            <h2 className="font-display font-light text-4xl md:text-6xl text-white leading-none mb-2">{artist.name}</h2>
            <p className="font-body text-gold text-xs md:text-sm tracking-[0.3em] uppercase">{artist.title}</p>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 md:p-10 grid md:grid-cols-3 gap-10">
          {/* Left: bio + gallery */}
          <div className="md:col-span-2 space-y-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-8 bg-gold/60" />
                <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Biography</span>
              </div>
              <div>
                {(artist.bio || "")
                  .split(/\n\s*\n|\n/)
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .map((para, i) => (
                    <p
                      key={i}
                      className="font-body text-white/65 text-sm md:text-[0.95rem] mb-4 last:mb-0"
                      style={{ textAlign: "justify", textJustify: "inter-word", hyphens: "auto", lineHeight: 1.8 }}
                    >
                      {para}
                    </p>
                  ))}
              </div>
            </div>

            {(artist.instagramUrl || (artist.instagramPosts && artist.instagramPosts.length > 0)) && (
              <InstagramFeed
                profileUrl={artist.instagramUrl}
                posts={artist.instagramPosts}
                artistName={artist.name}
              />
            )}

            {artist.galleryImages && artist.galleryImages.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-px w-8 bg-gold/60" />
                  <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Gallery</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {artist.galleryImages.slice(0, 4).map((img, i) => (
                    <div key={i} className="relative overflow-hidden rounded-sm bg-black/40 border border-gold/10">
                      <img
                        src={img}
                        alt={`${artist.name} ${i + 1}`}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        className="block w-full h-auto object-contain"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: stats + cta */}
          <div className="space-y-6">
            <div className="glass rounded-sm p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-5 bg-gold/60" />
                <span className="font-body text-gold text-[0.6rem] font-semibold tracking-[0.3em] uppercase">Career Stats</span>
              </div>
              <div className="space-y-4">
                {artist.stats.map((s, i) => (
                  <div key={s.label} className="flex justify-between items-center">
                    <span className="flex items-center gap-2 font-body text-white/45 text-xs tracking-wide">
                      <span className="text-gold/60">{statIcons[i] || statIcons[0]}</span>
                      {s.label}
                    </span>
                    <span className="stat-number text-2xl font-bold">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-gold/30 rounded-sm p-6 text-center">
              <p className="font-body text-white/40 text-xs tracking-wider mb-3 uppercase">Interested in booking?</p>
              <h3 className="font-display text-xl text-white mb-4">Work with {artist.name.split(" ")[0]}</h3>
              <button
                onClick={handleBook}
                className="btn-gold w-full py-3 text-xs rounded-sm flex items-center justify-center gap-2"
                data-testid="modal-request-booking"
              >
                <span>Request Booking</span>
                <ArrowRight size={13} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistModal;
